import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import { FaCheckCircle, FaCloudUploadAlt, FaEdit, FaSave, FaTrash, FaUndo } from 'react-icons/fa';
import useAlertNotification from '../../../context/AlertNotificationContext';
import ErrorRequestService from '../../../services/errorRequest';
import { MONTHS, parseForecastExcel } from '../../../services/forecast/forecastParser';
import { TIPO_PERIODO_OPTIONS, getPeriodoNumeroOptions, formatPeriodoLabel, getPrazoCadastro, isPeriodoDentroDoPrazo, encontrarPeriodoConflitante, encontrarPeriodoExato } from '../../../services/forecast/periodoUtils';
import ForecastApiService from '../../../services/api/ForecastApiService';
import './CriarForecastView.css';
import PageContainer from '../../../components/PageContainer';
import { MdClose, MdOutlineArrowBack, MdOutlineArrowForward } from 'react-icons/md';

// ─── helpers ────────────────────────────────────────────────────────────────

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const PCT = (v) => `${(Number(v) * 100).toFixed(0)}%`;

function probBadge(prob) {
    const pct = Math.round(prob * 100);
    let variant = 'secondary';
    if (pct >= 90) variant = 'success';
    else if (pct >= 70) variant = 'warning';
    else if (pct >= 60) variant = 'info';
    return <Badge variant={variant}>{pct}%</Badge>;
}

function extractYearFromFilename(name) {
    const match = name.match(/20\d{2}/);
    return match ? match[0] : '';
}

const ANO_MINIMO = 2026;
const ANO_MAXIMO = Math.max(ANO_MINIMO, new Date().getFullYear() + 5);
const YEAR_OPTIONS = Array.from({ length: ANO_MAXIMO - ANO_MINIMO + 1 }, (_, i) => ANO_MINIMO + i);

// ─── sub-components ──────────────────────────────────────────────────────────

function UploadArea({ onFile, onClose }) {
    const inputRef = useRef();
    const [dragging, setDragging] = useState(false);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) onFile(file);
    }, [onFile]);

    const handleChange = (e) => {
        if (e.target.files[0]) onFile(e.target.files[0]);
    };

    return (
        <div
            className={`forecast-upload-area${dragging ? ' dragging' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current.click()}
        >
            <button
                type="button"
                className="forecast-close-btn"
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                aria-label="Fechar"
                title="Fechar sem selecionar arquivo"
            >
                <MdClose />
            </button>
            <FaCloudUploadAlt size={48} className="mb-3 text-primary" />
            <p className="mb-1 font-weight-bold">Arraste a planilha aqui ou clique para selecionar</p>
            <p className="text-muted small mb-0">Formato aceito: .xlsx</p>
            <input
                ref={inputRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={handleChange}
            />
        </div>
    );
}

function EditableCell({ value, type = 'text', onChange }) {
    return (
        <Form.Control
            size="sm"
            type={type}
            value={value}
            onChange={(e) => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
            style={{ minWidth: type === 'number' ? 90 : 130 }}
        />
    );
}

function ReviewTable({ rows, editingIds, onRowChange, onToggleSelect, onToggleSelectAll }) {
    const allSelected = rows.length > 0 && rows.every(r => r.selected);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const updateField = (id, field, value) => onRowChange(id, field, value);
    const updateMes   = (id, mes, value)   => onRowChange(id, `meses.${mes}`, Number(value));

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const sortedRows = useMemo(() => {
        if (!sortConfig.key) return rows;
        return [...rows].sort((a, b) => {
            const av = a[sortConfig.key];
            const bv = b[sortConfig.key];
            if (typeof av === 'number' && typeof bv === 'number') {
                return sortConfig.direction === 'asc' ? av - bv : bv - av;
            }
            const as = String(av ?? '').toLowerCase();
            const bs = String(bv ?? '').toLowerCase();
            if (as < bs) return sortConfig.direction === 'asc' ? -1 : 1;
            if (as > bs) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [rows, sortConfig]);

    const sortIcon = (key) => {
        if (sortConfig.key !== key) return <span style={{ opacity: 0.35, marginLeft: 4 }}>↕</span>;
        return <span style={{ marginLeft: 4 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;
    };

    const thSortable = { cursor: 'pointer', userSelect: 'none' };

    return (
        <div className="forecast-table-wrapper">
            <Table size="sm" bordered hover className="forecast-review-table mb-0">
                <thead>
                    <tr>
                        <th className="col-check sticky-col">
                            <Form.Check
                                type="checkbox"
                                checked={allSelected}
                                onChange={onToggleSelectAll}
                                title="Selecionar todos"
                            />
                        </th>
                        <th className="sticky-col-2" style={{ width: 160, ...thSortable }} onClick={() => handleSort('cliente')}>
                            Cliente{sortIcon('cliente')}
                        </th>
                        <th className="sticky-col-3" style={{ width: 140, ...thSortable }} onClick={() => handleSort('categoria')}>
                            Categoria{sortIcon('categoria')}
                        </th>
                        <th className="sticky-col-4" style={{ width: 70, ...thSortable }} onClick={() => handleSort('probabilidade')}>
                            Prob.{sortIcon('probabilidade')}
                        </th>
                        <th className="sticky-col-5" style={{ width: 140, ...thSortable }} onClick={() => handleSort('gp')}>
                            GP{sortIcon('gp')}
                        </th>
                        {MONTHS.map(m => <th key={m.key} style={{ width: 110 }}>{m.label}</th>)}
                        <th style={{ width: 120 }}>Soma Total</th>
                        <th style={{ width: 120 }}>Soma Pond.</th>
                        <th style={{ width: 90 }}>Margem Liq.</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedRows.map((row) => {
                        const editing  = editingIds.has(row._id);
                        const trClass  = !row.selected ? 'table-secondary text-muted' : '';
                        return (
                            <tr key={row._id} className={trClass}>
                                <td className="col-check sticky-col">
                                    <Form.Check
                                        type="checkbox"
                                        checked={row.selected}
                                        onChange={() => onToggleSelect(row._id)}
                                    />
                                </td>
                                <td className="sticky-col-2">
                                    {editing
                                        ? <EditableCell value={row.cliente} onChange={v => updateField(row._id, 'cliente', v)} />
                                        : row.cliente
                                    }
                                </td>
                                <td className="sticky-col-3">
                                    {editing
                                        ? <EditableCell value={row.categoria} onChange={v => updateField(row._id, 'categoria', v)} />
                                        : row.categoria
                                    }
                                </td>
                                <td className="sticky-col-4 text-center">{probBadge(row.probabilidade)}</td>
                                <td className="sticky-col-5">
                                    {editing
                                        ? <EditableCell value={row.gp} onChange={v => updateField(row._id, 'gp', v)} />
                                        : row.gp
                                    }
                                </td>
                                {MONTHS.map(m => (
                                    <td key={m.key} className="text-right">
                                        {editing
                                            ? <EditableCell type="number" value={row.meses[m.key]} onChange={v => updateMes(row._id, m.key, v)} />
                                            : row.meses[m.key] > 0 ? BRL.format(row.meses[m.key]) : <span className="text-muted">—</span>
                                        }
                                    </td>
                                ))}
                                <td className="text-right font-weight-bold">{BRL.format(row.somaTotal)}</td>
                                <td className="text-right">{BRL.format(row.somaPonderada)}</td>
                                <td className="text-center">{PCT(row.margemLiquida)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
}

// ─── main component ──────────────────────────────────────────────────────────

const STEPS = { UPLOAD: 'upload', META: 'meta', REVIEW: 'review', SAVING: 'saving', DONE: 'done' };

function CriarForecastView() {
    const alert = useAlertNotification();

    const [step, setStep]             = useState(STEPS.UPLOAD);
    const [fileObj, setFileObj]       = useState(null);
    const [fileName, setFileName]     = useState('');
    const [ano, setAno]               = useState('');
    const [tipoPeriodo, setTipoPeriodo] = useState('');
    const [periodoNumero, setPeriodoNumero] = useState('');
    const [rows, setRows]             = useState([]);
    const [editingIds, setEditingIds] = useState(new Set());
    const [parseError, setParseError] = useState(null);
    const [parsing, setParsing]       = useState(false);
    const [periodosAtivos, setPeriodosAtivos] = useState([]);

    // ── effects ───────────────────────────────────────────────────────────────

    useEffect(() => {
        if (!ano) {
            setPeriodosAtivos([]);
            return;
        }
        let cancelado = false;
        ForecastApiService.listImportacoes({ ano })
            .then(({ data }) => { if (!cancelado) setPeriodosAtivos(data?.items || []); })
            .catch(() => { if (!cancelado) setPeriodosAtivos([]); });
        return () => { cancelado = true; };
    }, [ano]);

    // ── handlers ──────────────────────────────────────────────────────────────

    const handleFile = (file) => {
        if (!file.name.endsWith('.xlsx')) {
            setParseError('Formato inválido. Selecione um arquivo .xlsx.');
            return;
        }
        setParseError(null);
        setFileObj(file);
        setFileName(file.name);
        setAno(extractYearFromFilename(file.name));
        setTipoPeriodo('');
        setPeriodoNumero('');
        setStep(STEPS.META);
    };

    const handleRevisar = async () => {
        setParsing(true);
        setParseError(null);
        try {
            const parsed = await parseForecastExcel(fileObj, tipoPeriodo, periodoNumero);
            if (parsed.length === 0) {
                setParseError(`Nenhuma linha encontrada com os filtros: Oferta = Serviço, Probabilidade > 50%, GP preenchido e valores em meses do período selecionado (${formatPeriodoLabel(tipoPeriodo, periodoNumero)}).`);
                setParsing(false);
                return;
            }
            setRows(parsed);
            setStep(STEPS.REVIEW);
        } catch (err) {
            setParseError(err.message || 'Erro ao processar a planilha.');
        } finally {
            setParsing(false);
        }
    };

    const handleRowChange = (id, field, value) => {
        setRows(prev => prev.map(r => {
            if (r._id !== id) return r;
            if (field.startsWith('meses.')) {
                const mes = field.split('.')[1];
                return { ...r, meses: { ...r.meses, [mes]: value } };
            }
            return { ...r, [field]: value };
        }));
    };

    const handleToggleSelect = (id) => {
        setRows(prev => prev.map(r => r._id === id ? { ...r, selected: !r.selected } : r));
        const row = rows.find(r => r._id === id);
        if (row?.selected) {
            setEditingIds(prev => { const next = new Set(prev); next.delete(id); return next; });
        }
    };

    const handleToggleSelectAll = () => {
        const allSelected = rows.every(r => r.selected);
        setRows(prev => prev.map(r => ({ ...r, selected: !allSelected })));
        if (allSelected) setEditingIds(new Set());
    };

    const handleEditSelected = () => {
        const selectedIds = new Set(rows.filter(r => r.selected).map(r => r._id));
        setEditingIds(prev => {
            const allAlreadyEditing = [...selectedIds].every(id => prev.has(id));
            if (allAlreadyEditing) {
                const next = new Set(prev);
                selectedIds.forEach(id => next.delete(id));
                return next;
            }
            return new Set([...prev, ...selectedIds]);
        });
    };

    const handleDeleteSelected = () => {
        setRows(prev => prev.filter(r => !r.selected));
        setEditingIds(prev => {
            const next = new Set(prev);
            rows.filter(r => r.selected).forEach(r => next.delete(r._id));
            return next;
        });
    };

    const handleReset = () => {
        setStep(STEPS.UPLOAD);
        setFileObj(null);
        setRows([]);
        setEditingIds(new Set());
        setFileName('');
        setAno('');
        setTipoPeriodo('');
        setPeriodoNumero('');
        setParseError(null);
    };

    // Fecha o card de upload/seleção de período sem subir/selecionar nada — volta para a
    // tela inicial do Criar Forecast (não usa history.goBack(): o histórico de navegação
    // real do usuário nem sempre tem uma entrada útil dentro do app).
    const handleFecharFluxo = () => handleReset();

    const handleSave = async () => {
        const selected = rows.filter(r => r.selected);
        if (selected.length === 0) {
            alert.warning('Selecione ao menos uma linha para salvar.');
            return;
        }
        setStep(STEPS.SAVING);
        try {
            const payload = selected.map(({ _id, selected: _s, oferta: _o, ...rest }) => rest);
            await ForecastApiService.saveForecast({
                itens: payload,
                arquivo: fileName,
                ano: Number(ano),
                tipoPeriodo,
                periodoNumero: tipoPeriodo === 'ANO' ? null : Number(periodoNumero),
            });
            setStep(STEPS.DONE);
        } catch (err) {
            const errorService = new ErrorRequestService(err);
            alert.error(errorService.getErrors());
            setStep(STEPS.REVIEW);
        }
    };

    // ── derived ───────────────────────────────────────────────────────────────

    const periodoCompleto = Boolean(tipoPeriodo) && Boolean(ano)
        && (tipoPeriodo === 'ANO' || Boolean(periodoNumero));
    const prazoExpirado = periodoCompleto && !isPeriodoDentroDoPrazo(tipoPeriodo, periodoNumero, ano);
    const periodoConflitante = periodoCompleto
        ? encontrarPeriodoConflitante(tipoPeriodo, periodoNumero, periodosAtivos)
        : null;
    const periodoExato = periodoCompleto && !periodoConflitante && !prazoExpirado
        ? encontrarPeriodoExato(tipoPeriodo, periodoNumero, periodosAtivos)
        : null;
    const metaValid     = periodoCompleto && !prazoExpirado && !periodoConflitante;
    const selectedCount = rows.filter(r => r.selected).length;
    const allSelectedEditing = selectedCount > 0 && rows.filter(r => r.selected).every(r => editingIds.has(r._id));

    // ── button actions ───────────────────────────────────────────────────────────────

    const actions = [
        {
            label: 'Voltar',
            icon: <MdOutlineArrowBack />,
            color: 'info',
            type: 'button',
            onClick: () => setStep(STEPS.META)
        },
        {
            label: 'Salvar',
            icon: <FaSave />,
            color: 'primary',
            style: {"margin-left": "10px"},
            type: 'button',
            onClick: handleSave
        }
    ];

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <PageContainer title="Criar Forecast" actions={step === STEPS.REVIEW ? actions : []}>
{/*             <div className="d-flex" style={{ gap: 8 }}>
                <Button variant="outline-secondary" size="md" onClick={() => setStep(STEPS.META)}>
                    <MdOutlineArrowBack className="mr-1" /> Voltar
                </Button>
                <Button variant="primary" size="md" onClick={handleSave} disabled={selectedCount === 0}>
                    <FaSave className="mr-1" /> Salvar {selectedCount > 0 ? `(${selectedCount})` : ''}
                </Button>
            </div> */}

            {/* ── UPLOAD ── */}
            {step === STEPS.UPLOAD && (
                <>
                    {parseError && (
                        <Alert variant="danger" onClose={() => setParseError(null)} dismissible className="mb-3">
                            {parseError}
                        </Alert>
                    )}
                    <UploadArea onFile={handleFile} onClose={handleFecharFluxo} />
                </>
            )}

            {/* ── META ── */}
            {step === STEPS.META && (
                <div className="forecast-meta-wrapper">
                    <div className="forecast-meta-card">
                        <button
                            type="button"
                            className="forecast-close-btn"
                            onClick={handleFecharFluxo}
                            aria-label="Fechar"
                            title="Fechar sem selecionar o período"
                        >
                            <MdClose />
                        </button>
                        <h6 className="mb-4 font-weight-bold">Confirme os dados da importação</h6>

                        <Form.Group>
                            <Form.Label className="small text-muted mb-1">Arquivo selecionado</Form.Label>
                            <Form.Control readOnly value={fileName} size="sm" className="text-muted" />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>
                                Período a considerar <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="d-flex" style={{ gap: 8 }}>
                                {TIPO_PERIODO_OPTIONS.map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className={`forecast-periodo-btn${tipoPeriodo === opt.value ? ' active' : ''}`}
                                        onClick={() => { setTipoPeriodo(opt.value); setPeriodoNumero(''); }}
                                    >
                                        <span className="d-block font-weight-bold">{opt.label}</span>
                                        <span className="d-block small">{opt.sub}</span>
                                    </button>
                                ))}
                            </div>
                            {!tipoPeriodo && (
                                <p className="text-danger small mt-1 mb-0">Selecione o período a considerar.</p>
                            )}
                        </Form.Group>

                        {tipoPeriodo && (
                            <Form.Group>
                                <Form.Label>
                                    Ano <span className="text-danger">*</span>
                                    {!ano && (
                                        <span className="text-muted small ml-2">
                                            (não encontrado no nome do arquivo)
                                        </span>
                                    )}
                                </Form.Label>
                                <Form.Control
                                    as="select"
                                    value={ano}
                                    onChange={e => setAno(e.target.value)}
                                    isInvalid={!ano}
                                >
                                    <option value="">Selecione o ano…</option>
                                    {YEAR_OPTIONS.map(y => (
                                        <option key={y} value={String(y)}>{y}</option>
                                    ))}
                                </Form.Control>
                                <Form.Control.Feedback type="invalid">
                                    Selecione o ano do forecast.
                                </Form.Control.Feedback>
                            </Form.Group>
                        )}

                        {tipoPeriodo === 'ANO' && ano && (
                            <p className="text-muted small">Ano completo selecionado (Jan – Dez).</p>
                        )}

                        {tipoPeriodo && getPeriodoNumeroOptions(tipoPeriodo).length > 0 && (
                            <Form.Group>
                                <Form.Label>
                                    {tipoPeriodo === 'SEMESTRE' ? 'Semestre' : 'Trimestre'} <span className="text-danger">*</span>
                                </Form.Label>
                                <div className="d-flex" style={{ gap: 8 }}>
                                    {getPeriodoNumeroOptions(tipoPeriodo).map(opt => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            className={`forecast-periodo-btn${periodoNumero === opt.value ? ' active' : ''}`}
                                            onClick={() => setPeriodoNumero(opt.value)}
                                        >
                                            <span className="d-block font-weight-bold">{opt.label}</span>
                                            <span className="d-block small">{opt.sub}</span>
                                        </button>
                                    ))}
                                </div>
                                {!periodoNumero && (
                                    <p className="text-danger small mt-1 mb-0">
                                        Selecione o {tipoPeriodo === 'SEMESTRE' ? 'semestre' : 'trimestre'}.
                                    </p>
                                )}
                            </Form.Group>
                        )}

                        {periodoConflitante && (
                            <Alert variant="danger">
                                Já existe um forecast ativo para "{formatPeriodoLabel(periodoConflitante.tipo_periodo, periodoConflitante.periodo_numero)} de {ano}"{' '}
                                que conflita com o período selecionado ("{formatPeriodoLabel(tipoPeriodo, periodoNumero)} de {ano}").
                            </Alert>
                        )}

                        {!periodoConflitante && prazoExpirado && (
                            <Alert variant="danger">
                                O prazo para cadastro do {formatPeriodoLabel(tipoPeriodo, periodoNumero)} de {ano} encerrou em{' '}
                                {getPrazoCadastro(tipoPeriodo, periodoNumero, ano).toLocaleDateString('pt-BR')}{' '}
                                (20 dias após o fim do período). Não é possível cadastrar este período.
                            </Alert>
                        )}

                        {periodoExato && (
                            <Alert variant="warning">
                                Já existe um forecast ativo para "{formatPeriodoLabel(tipoPeriodo, periodoNumero)} de {ano}"{' '}
                                ({periodoExato.arquivo}, importado em {new Date(periodoExato.importado_em).toLocaleDateString('pt-BR')}).{' '}
                                Ao salvar, essa importação será desativada e substituída pela nova.
                            </Alert>
                        )}

                        {parseError && (
                            <Alert variant="danger" onClose={() => setParseError(null)} dismissible>
                                {parseError}
                            </Alert>
                        )}

                        <div className="d-flex justify-content-between mt-4">
                            <Button variant="outline-secondary" size="md" onClick={handleReset} disabled={parsing}>
                                <FaUndo className="mr-1" /> Trocar arquivo
                            </Button>
                            <Button
                                variant="primary"
                                size="md"
                                disabled={!metaValid || parsing}
                                onClick={handleRevisar}
                            >
                                {parsing
                                    ? <><Spinner animation="border" size="sm" className="mr-1" /> Lendo planilha…</>
                                    : <><MdOutlineArrowForward className="mr-1" /> Revisar linhas</>
                                }
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── REVIEW ── */}
            {step === STEPS.REVIEW && (
                <>
                    <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap" style={{ gap: 8 }}>
                        <div>
                            <Badge variant="primary" className="mr-2 p-2">{rows.length} linhas</Badge>
                            <Badge variant="success" className="mr-2 p-2">{selectedCount} selecionadas</Badge>
                            <Badge variant="light" className="mr-2 border p-2">{ano} - {formatPeriodoLabel(tipoPeriodo, periodoNumero)}</Badge>
                            <span className="text-muted small">{fileName}</span>
                        </div>
                        <div className="d-flex" style={{ gap: 8 }}>
                            <Button
                                variant={allSelectedEditing ? 'warning' : 'outline-warning'}
                                size="sm"
                                disabled={selectedCount === 0}
                                onClick={handleEditSelected}
                                title="Editar linhas selecionadas"
                            >
                                <FaEdit className="mr-1" />
                                {allSelectedEditing ? 'Concluir edição' : 'Editar'}
                            </Button>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                disabled={selectedCount === 0}
                                onClick={handleDeleteSelected}
                                title="Excluir linhas selecionadas"
                            >
                                <FaTrash className="mr-1" /> Excluir
                            </Button>
                        </div>
                    </div>

                    <ReviewTable
                        rows={rows}
                        editingIds={editingIds}
                        onRowChange={handleRowChange}
                        onToggleSelect={handleToggleSelect}
                        onToggleSelectAll={handleToggleSelectAll}
                    />
                </>
            )}

            {/* ── SAVING ── */}
            {step === STEPS.SAVING && (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Salvando forecast…</p>
                </div>
            )}

            {/* ── DONE ── */}
            {step === STEPS.DONE && (
                <div className="text-center py-5">
                    <FaCheckCircle size={56} className="text-success mb-3" />
                    <h5>Forecast salvo com sucesso!</h5>
                    <p className="text-muted">{selectedCount} itens registrados — {ano}, {formatPeriodoLabel(tipoPeriodo, periodoNumero)}.</p>
                    <Button variant="outline-primary" onClick={handleReset}>
                        Importar nova planilha
                    </Button>
                </div>
            )}

        </PageContainer>
    );
}

export default CriarForecastView;
